import {ReportRequestParametersObjectObject, SavedReportBean} from 'common/api/api.ts'
import dayjs from 'dayjs'

import {FilterKV} from './types.ts'

export const normalizeReportType = (raw: unknown): ReportRequestParametersObjectObject.TypeEnum =>
    String(raw || '').toLowerCase() === 'tracker'
        ? ReportRequestParametersObjectObject.TypeEnum.Tracker
        : ReportRequestParametersObjectObject.TypeEnum.Stats

export const readSavedReportType = (entity: any): ReportRequestParametersObjectObject.TypeEnum | undefined => {
    const value = entity?.reportType ?? entity?.type
    if (value === undefined || value === null) {
        return undefined
    }
    return normalizeReportType(value)
}

export const filtersObjectToArray = (filtersObj: Record<string, string[]> | undefined): FilterKV[] => {
    if (!filtersObj) {
        return [{dimension: undefined, value: undefined}]
    }

    const result: FilterKV[] = []
    Object.entries(filtersObj).forEach(([dimension, values]) => {
        if (Array.isArray(values)) {
            values.forEach((value) => {
                result.push({dimension, value})
            })
        }
    })

    return result.length > 0 ? result : [{dimension: undefined, value: undefined}]
}

export const filtersToObject = (filters: FilterKV[]) =>
    filters.reduce(
        (acc, filter) => {
            if (!filter.dimension || filter.value == null || filter.value === '') {
                return acc
            }

            const dimension = filter.dimension

            if (!acc[dimension]) {
                acc[dimension] = []
            }

            const values = Array.isArray(filter.value) ? filter.value : [filter.value]
            values.forEach((value) => {
                const normalizedValue = String(value)
                if (!normalizedValue) {
                    return
                }
                if (!acc[dimension].includes(normalizedValue)) {
                    acc[dimension].push(normalizedValue)
                }
            })

            return acc
        },
        {} as Record<string, string[]>,
    )

export const getDefaultSelections = (reportType: ReportRequestParametersObjectObject.TypeEnum) => ({
    dimensions: reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker ? ['Time'] : ['Date'],
    metrics: reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker ? [] : ['Bid', 'Impression'],
})

export const mapSavedReportToFormState = (
    bean: SavedReportBean,
    fallbackReportType: ReportRequestParametersObjectObject.TypeEnum,
) => {
    const reportType = readSavedReportType(bean) ?? fallbackReportType
    const period = bean.reportPeriod

    const startDate =
        period === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && bean.startDate
            ? dayjs(bean.startDate, 'YYYY-MM-DD')
            : undefined
    const endDate =
        period === SavedReportBean.ReportPeriodEnum.CUSTOMRANGE && bean.endDate
            ? dayjs(bean.endDate, 'YYYY-MM-DD')
            : undefined

    return {
        reportType,
        formValues: {
            ...bean,
            startDate,
            endDate,
            dateRange: startDate && endDate ? [startDate, endDate] : undefined,
        },
        dimensions: (bean.dimensions as unknown as string[]) ?? [],
        metrics:
            reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker
                ? []
                : ((bean.metrics as unknown as string[]) ?? []),
        filters: filtersObjectToArray(bean.filters),
        events: bean.events || [],
        name: bean.name,
    }
}

interface BuildSavedReportBodyParams {
    title: string
    reportType: ReportRequestParametersObjectObject.TypeEnum
    selectedPeriod?: SavedReportBean.ReportPeriodEnum
    startDate?: dayjs.Dayjs
    endDate?: dayjs.Dayjs
    selectedTimezone?: string
    selectedKeyColumns: string[]
    selectedValueColumns: string[]
    selectedEventColumns: string[]
    selectedFilters: FilterKV[]
}

export const buildSavedReportBody = ({
                                         title,
                                         reportType,
                                         selectedPeriod,
                                         startDate,
                                         endDate,
                                         selectedTimezone,
                                         selectedKeyColumns,
                                         selectedValueColumns,
                                         selectedEventColumns,
                                         selectedFilters,
                                     }: BuildSavedReportBodyParams) =>
    ({
        name: title,
        reportPeriod: selectedPeriod as SavedReportBean.ReportPeriodEnum,
        startDate: startDate?.format('YYYY-MM-DD'),
        endDate: endDate?.format('YYYY-MM-DD'),
        timezoneId: selectedTimezone || '',
        dimensions: selectedKeyColumns as unknown as string[],
        metrics: reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker ? [] : selectedValueColumns,
        events: selectedEventColumns,
        filters: filtersToObject(selectedFilters),
        reportType: reportType === ReportRequestParametersObjectObject.TypeEnum.Tracker ? 'Tracker' : 'Stats',
    } as any)
